/**
 * @file Ulid class that wraps the functionality of ULID (Universally Unique Lexicographically Sortable Identifier)
 * from the `id128` package. It provides methods to generate new ULIDs and parse existing ones,
 * along with a convenient method to get the time associated with a ULID.
 */
import { Ulid as UlidBase, UlidMonotonic as UlidMonotonicBase } from 'id128';
import { toLog } from '@codegrade/logging';
/** The base class for Ulid errors. */
export class UlidError extends Error {
}
/** The error thrown when parsing fails. */
export class InvalidUlidError extends UlidError {
}
/**
 * Parses a ULID string (in canonical form) and returns a corresponding ULID object.
 *
 * @param value The ULID string in canonical format.
 * @returns A ULID instance parsed from the provided string.
 */
function parse(value) {
    try {
        return UlidBase.fromCanonical(value);
    }
    catch (caught) {
        const error = caught instanceof Error ? caught : new Error(String(caught));
        throw new InvalidUlidError(error.message, { cause: error });
    }
}
/** A class representing a ULID (Universally Unique Lexicographically Sortable Identifier). */
export class Ulid {
    value;
    /**  Represents the maximum possible ULID value. */
    static MAX = /* #__PURE */ new Ulid(UlidBase.MAX());
    /**  Represents the minimum possible ULID value. */
    static MIN = /* #__PURE */ new Ulid(UlidBase.MIN());
    /**
     * Get the maximum Ulid for a certain time.
     *
     * @param time The time to get the maximum Ulid for.
     * @returns The maximum possible Ulid for a certain time.
     */
    static getMaxForTime(time) {
        const randomPart = UlidBase.MAX().toCanonical().slice(10);
        const timePart = UlidBase.generate({ time }).toCanonical().slice(0, 10);
        return Ulid.parse(`${timePart}${randomPart}`);
    }
    /**
     * Private constructor for creating a ULID instance from a base ULID object.
     *
     * Use static methods `generate`, `parse` to create instances, or use `MIN` or `MAX` for
     * constants.
     *
     * @param value The underlying ULID value from the `id128` package.
     */
    constructor(value) {
        this.value = value;
        Object.freeze(this);
    }
    /** @returns The cannonical representation of a ULID in string form. */
    toCanonical() {
        return this.value.toCanonical();
    }
    /**
     * Retrieves the time associated with the ULID.
     *
     * @returns The Date object representing the time encoded in the ULID.
     */
    get time() {
        return this.value.time;
    }
    /** @returns A log representation of this class. */
    [toLog]() {
        return {
            type: this.constructor.name,
            value: this.toCanonical(),
        };
    }
    /**
     * Generates a new ULID.
     *
     * @returns A newly generated ULID instance.
     */
    static generate() {
        return new Ulid(UlidBase.generate());
    }
    /**
     * Parses a ULID string (in canonical form) and returns a corresponding ULID object.
     *
     * @param value The ULID string in canonical format.
     * @returns A ULID instance parsed from the provided string.
     */
    static parse(value) {
        return new Ulid(parse(value));
    }
    /**
     * Convert this unnamed ulid to a named one.
     *
     * @param name The name to use.
     * @returns A new named ulid.
     */
    withName(name) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return new NamedUlid(name, this.value);
    }
}
/** A class for a ulid with a named tag in it. */
export class NamedUlid {
    name;
    value;
    /**
     * @param name The name of this ULID.
     * @param value The internal ULID value.
     */
    constructor(name, value) {
        this.name = name;
        this.value = value;
        Object.freeze(this);
    }
    /** @returns The cannonical representation of a NameUlid in string form. */
    toCanonical() {
        return `${this.name}_${this.value.toCanonical()}`;
    }
    /**
     * Represents the maximum possible ULID value.
     *
     * @param name The name to use.
     * @returns The maximum possible NameUlid value.
     */
    static getMax(name) {
        return new NamedUlid(name, UlidBase.MAX());
    }
    /**
     * Get the maximum Ulid for a certain time.
     *
     * @param name The name for the generated Ulid.
     * @param time The time to get the maximum Ulid for.
     * @returns The maximum possible Ulid for a certain time.
     */
    static getMaxForTime(name, time) {
        return Ulid.getMaxForTime(time).withName(name);
    }
    /**
     * Represents the minimum possible ULID value.
     *
     * @param name The name to use.
     * @returns The minimum possible NameUlid value.
     */
    static getMin(name) {
        return new NamedUlid(name, UlidBase.MIN());
    }
    /**
     * Retrieves the time associated with the ULID.
     *
     * @returns The Date object representing the time encoded in the ULID.
     */
    get time() {
        return this.value.time;
    }
    /**
     * Check if the other named ulid has the same value as this one.
     *
     * @param other The other to compare with.
     * @returns If they are the same.
     */
    equals(other) {
        return this.name === other.name && this.value.compare(other.value) === 0;
    }
    /** @returns A log representation of this class. */
    [toLog]() {
        return {
            type: this.constructor.name,
            value: this.toCanonical(),
            name: this.name,
        };
    }
    /**
     * Generates a new ULID.
     *
     * @param name The name of the ULID to generate.
     * @returns A newly generated ULID instance.
     */
    static generate(name) {
        return new NamedUlid(name, UlidBase.generate());
    }
    /**
     * Generates a new monotonic ULID.
     *
     * This method should be used if it is extremely important that if we have Ulid1, Ulid2 where
     * Ulid1 is generated before Ulid2 that Ulid1 is always smaller, even if they are generated in
     * the same millisecond. This increases the guessability of the generated identifier.
     *
     * @param name The name of the ULID to generate.
     * @returns A newly generated ULID instance.
     */
    static generateMonotonic(name) {
        return new NamedUlid(name, UlidMonotonicBase.generate());
    }
    /**
     * Parses a ULID string (in canonical form) and returns a corresponding ULID object.
     *
     * @param name The name of the ULID to parse.
     * @param value The ULID string in canonical format.
     * @returns A ULID instance parsed from the provided string.
     */
    static parse(name, value) {
        const prefix = `${name}_`;
        if (!value.startsWith(prefix)) {
            throw new InvalidUlidError(`Given ulid (${value}) does not start with name: ${name}`);
        }
        return new NamedUlid(name, parse(value.slice(prefix.length)));
    }
    /** @returns This named ulid as an unnamed one. */
    withoutName() {
        return new Ulid(this.value);
    }
}
